// Recrutement phase 5 — onboarding (port rec9-onboard.jsx): cohorte de
// recrues (cartes par phase du parcours), détail plein écran avec ligne
// de parcours, plan J1→J90, dossier admin, ressources, équipe autour de
// la recrue, et le handoff qui crée la fiche Employé (période d'essai
// initialisée → module Contrats).
//
// Deviations du proto (délibérées):
// - « Offre signée » = date réelle d'acceptation (createdAt de
//   l'onboarding, clampée avant J1) au lieu d'un offset fictif J−18;
//   le nœud « Pré-boarding » du proto disparaît (5 nœuds au lieu de 6),
// - le buddy se choisit dans un vrai sélecteur staff (pas un toast),
// - s'ajoutent le bloc 1:1 (spec écran 13) et le handoff → Employee.
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { ENTITIES, REC_OB_HORIZONS, REC_OB_OWNERS, REC_OB_PHASES, type RecObPhaseId } from '@pointage/shared';
import type { RecInterviewerDTO, RecObTaskDTO, RecOnboardingDTO } from '../../api/hooks.js';
import { RecEnt, recFmtDate } from './common.js';
import { ProbationSection, ProbationStrip } from './Probation.js';

function entOf(id: string) {
  return ENTITIES.find((e) => e.id === id);
}

// ── helpers parcours ────────────────────────────────────────
const DAY = 864e5;

/** J relatif au démarrage: négatif avant J1 (J−n), 0 = jour 1. */
export function obDays(ob: RecOnboardingDTO): number {
  return Math.round((Date.now() - new Date(`${ob.start}T00:00:00`).getTime()) / DAY);
}
export function obPhase(ob: RecOnboardingDTO): RecObPhaseId {
  const d = obDays(ob);
  return d < 0 ? 'pre' : d < 14 ? 'j1' : d < 60 ? 'm1' : 'j90';
}
export function obPct(ob: RecOnboardingDTO): number {
  const all = Object.values(ob.plan).flat();
  return all.length ? Math.round((all.filter((t) => t.done).length / all.length) * 100) : 0;
}
export function obLate(ob: RecOnboardingDTO): number {
  const d = obDays(ob);
  let n = 0;
  for (const h of REC_OB_HORIZONS) {
    if (d > h.due) n += (ob.plan[h.id] ?? []).filter((t) => !t.done).length;
  }
  return n;
}
export function obNext(ob: RecOnboardingDTO): RecObTaskDTO | null {
  for (const h of REC_OB_HORIZONS) {
    const t = (ob.plan[h.id] ?? []).find((x) => !x.done);
    if (t) return t;
  }
  return null;
}
export function obDocsMissing(ob: RecOnboardingDTO): number {
  return ob.dossier.filter((x) => x.st !== 'ok').length;
}

const obJ = (d: number) => (d < 0 ? `J−${-d}` : `J+${d}`);

function ObAv({ ob, big }: { ob: RecOnboardingDTO; big?: boolean }) {
  return (
    <span className={big ? 'r3-bigav' : 'rec-av'} style={{ background: ob.candidate.avColor }}>
      {ob.candidate.firstName[0]}{ob.candidate.lastName[0]}
    </span>
  );
}

function ObOwner({ ow }: { ow: string }) {
  return <span className="ob-ow" style={{ '--oc': REC_OB_OWNERS[ow] ?? '#74747e' } as React.CSSProperties}>{ow}</span>;
}

// ── carte recrue (cohorte) ──────────────────────────────────
function ObCard({ ob, i, onOpen }: { ob: RecOnboardingDTO; i: number; onOpen: () => void }) {
  const d = obDays(ob);
  const ph = REC_OB_PHASES[obPhase(ob)];
  const pct = obPct(ob);
  const manq = ob.dossier.filter((x) => x.st === 'manq').length;
  const recu = ob.dossier.filter((x) => x.st === 'recu').length;
  const late = obLate(ob);
  const next = obNext(ob);
  const ent = entOf(ob.entityId);
  return (
    <div className="ob-card" style={{ '--pc': ph.c, animationDelay: `${i * 80}ms` } as React.CSSProperties} onClick={onOpen}>
      <div className="ob-top">
        <ObAv ob={ob} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="nm">{ob.candidate.firstName} {ob.candidate.lastName}</div>
          <div className="rl">{ob.roleLabel}{ent && <RecEnt id={ent.id} name={ent.name} color={ent.color} />}</div>
        </div>
        <span className="ob-ph">{ph.ic} {ph.lbl}</span>
      </div>
      <div className="ob-count">
        <span className="j">{obJ(d)}</span>
        <span className="w">{d < 0 ? <>démarre le <b>{recFmtDate(ob.start)}</b></> : <>en poste depuis le <b>{recFmtDate(ob.start)}</b></>}</span>
        <span className="p">{pct}%<i>du plan</i></span>
      </div>
      <div className="ob-segs">
        {REC_OB_HORIZONS.map((h) => {
          const ts = ob.plan[h.id] ?? [];
          const dn = ts.filter((t) => t.done).length;
          return (
            <div key={h.id} className="ob-seg">
              <span className="tr"><i style={{ width: `${ts.length ? (dn / ts.length) * 100 : 0}%` }}></i></span>
              <span className="lb"><b>{h.id}</b><span>{dn}/{ts.length}</span></span>
            </div>
          );
        })}
      </div>
      <div className="ob-chips">
        {manq > 0 ? <span className="ob-chip warn">🗂 {manq} document{manq > 1 ? 's' : ''} manquant{manq > 1 ? 's' : ''}</span>
          : recu > 0 ? <span className="ob-chip warn">🗂 {recu} pièce{recu > 1 ? 's' : ''} à valider</span>
            : <span className="ob-chip ok">🗂 dossier complet</span>}
        {late > 0 && <span className="ob-chip warn">⏰ {late} tâche{late > 1 ? 's' : ''} en retard</span>}
        {ob.buddy ? <span className="ob-chip">🤝 buddy · {ob.buddy.name}</span> : <span className="ob-chip warn">🤝 buddy à désigner</span>}
        {ob.employee && <span className="ob-chip ok">🪪 fiche {ob.employee.code}</span>}
      </div>
      <div style={{ marginTop: 12 }}><ProbationStrip ob={ob} /></div>
      {next && (
        <div className="ob-next">
          <span className="k">Prochaine étape</span>
          <span className="t">{next.tx}</span>
          <ObOwner ow={next.ow} />
        </div>
      )}
      <div className="ob-foot">
        <button className="rec-go sm">Ouvrir le parcours →</button>
        {ob.manager && <span className="mgr">manager · {ob.manager}</span>}
      </div>
    </div>
  );
}

// ── détail plein écran ──────────────────────────────────────
export interface ObActions {
  onTask: (obId: string, hz: string, taskId: string, done: boolean) => void;
  onDoc: (obId: string, docId: string, st: 'manq' | 'recu' | 'ok') => void;
  onResource: (obId: string, index: number) => void;
  onBuddy: (obId: string, userId: string | null) => void;
  onRemindDocs: (obId: string) => void;
  onHandoff: (obId: string) => void;
}

function ObJourney({ ob }: { ob: RecOnboardingDTO }) {
  const st = new Date(`${ob.start}T00:00:00`).getTime();
  const now = Date.now();
  const d = obDays(ob);
  // « Offre signée » = acceptation réelle, clampée avant J1 pour garder
  // la ligne monotone quand l'offre s'accepte tout près du démarrage.
  const signed = Math.min(new Date(ob.createdAt).getTime(), st - DAY);
  const nodes = [
    { lbl: 'Offre signée', ic: '✍️', t: signed },
    { lbl: 'Jour 1', ic: '🚀', t: st },
    { lbl: 'Semaine 1', ic: '🧭', t: st + 7 * DAY },
    { lbl: 'Mois 1', ic: '📈', t: st + 30 * DAY },
    { lbl: 'Jour 90 · revue', ic: '🎓', t: st + 90 * DAY },
  ];
  let p = 0;
  for (let i = 0; i < nodes.length - 1; i++) {
    if (now >= nodes[i + 1]!.t) p = i + 1;
    else if (now >= nodes[i]!.t) { p = i + (now - nodes[i]!.t) / (nodes[i + 1]!.t - nodes[i]!.t); break; }
  }
  const pct = Math.min(100, Math.max(2, (p / (nodes.length - 1)) * 100));
  return (
    <div className="ob9-journey">
      <div className="ob9-jh">
        <h4>Parcours d’intégration</h4>
        <span className="k">modèle « {ob.roleLabel} »</span>
        <div className="ob9-today">Aujourd’hui : <b>{obJ(d)}</b></div>
      </div>
      <div className="ob9-line">
        <i className="fill" style={{ width: `${pct}%` }}></i>
        <span className="mark" style={{ left: `${pct}%` }}><b>aujourd’hui</b></span>
        {nodes.map((n, i) => {
          const x = (i / (nodes.length - 1)) * 100;
          const done = now >= n.t;
          const cur = done && (i === nodes.length - 1 || now < nodes[i + 1]!.t);
          return (
            <span key={i} className={`ob9-node${done ? ' done' : ''}${cur ? ' cur' : ''}`} style={{ left: `${x}%` }}>
              <span className="dot">{done && !cur ? '✓' : n.ic}</span>
              <span className="lb">{n.lbl}</span>
              <span className="dt">{new Date(n.t).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

function ObDetail({ ob, canEdit, canEmp, busy, interviewers, act }: {
  ob: RecOnboardingDTO; canEdit: boolean; canEmp: boolean; busy: boolean;
  interviewers: RecInterviewerDTO[]; act: ObActions;
}) {
  const d = obDays(ob);
  const docsOk = ob.dossier.filter((x) => x.st === 'ok').length;
  const manq = ob.dossier.filter((x) => x.st === 'manq').length;
  const [buddyPick, setBuddyPick] = useState(false);
  const ent = entOf(ob.entityId);
  return (
    <div>
      <ObJourney ob={ob} />
      <div className="r7-grid" style={{ maxWidth: 'none' }}>
        <div className="r7-col">
          <div className="r7-sec">
            <h4>✅ Plan d’intégration<span className="ob9-pctb">{obPct(ob)}%</span></h4>
            {REC_OB_HORIZONS.map((h) => {
              const ts = ob.plan[h.id] ?? [];
              const dn = ts.filter((t) => t.done).length;
              const lateH = d > h.due;
              return (
                <div key={h.id} className="ob9-hz">
                  <div className="ob9-hzh">
                    <span className="b" style={{ '--hc': h.c } as React.CSSProperties}>{h.id}</span>
                    <b>{h.lbl}</b>
                    <span className="due">avant J+{h.due}</span>
                    <span className="n">{dn}/{ts.length}</span>
                    <span className="tr" style={{ '--hc': h.c } as React.CSSProperties}><i style={{ width: `${ts.length ? (dn / ts.length) * 100 : 0}%` }}></i></span>
                  </div>
                  {ts.map((t) => (
                    <div key={t.id} className={`rec-task${t.done ? ' done' : ''}`}>
                      <button
                        className={`rec-chk${t.done ? ' on' : ''}`}
                        disabled={!canEdit || busy}
                        title={t.done && t.doneBy ? `fait par ${t.doneBy}` : undefined}
                        onClick={() => act.onTask(ob.id, h.id, t.id, !t.done)}
                      >{t.done ? '✓' : ''}</button>
                      <span className="tx">{t.tx}{lateH && !t.done && <span className="ob9-late">en retard</span>}</span>
                      <ObOwner ow={t.ow} />
                    </div>
                  ))}
                </div>
              );
            })}
            <span className="rec-hint">Points d’essai J+15 → décision finale dans l’onglet « Période d’essai »</span>
          </div>
          <div className="r7-sec">
            <h4>🗓 1:1 — alignés sur la période d’essai</h4>
            {ob.oneOnOne.map((o, i) => (
              <div key={i} className="ob9-oo">
                <span className="w">{o.when}</span>
                <span className="a">{o.agenda}</span>
              </div>
            ))}
            {!ob.oneOnOne.length && <div className="rec-emptybox">Pas d’agenda 1:1 dans le modèle de ce rôle.</div>}
          </div>
        </div>
        <div className="r7-col">
          <div className="r7-sec">
            <h4>🗂 Dossier administratif
              {canEdit && manq > 0 && (
                <button className="rec-ghost sm" style={{ marginLeft: 'auto' }} disabled={busy} onClick={() => act.onRemindDocs(ob.id)}>
                  ✉️ Relancer ({manq})
                </button>
              )}
            </h4>
            <div className="ob9-docs-h">
              <span className="ob9-ring">{ob.dossier.length ? Math.round((docsOk / ob.dossier.length) * 100) : 0}%</span>
              <div>
                <b>{docsOk}/{ob.dossier.length} validés</b>
                <span>modèle « dossier admin · Algérie · {ob.contrat} »<br />relance email en un clic — pièces manquantes listées</span>
              </div>
            </div>
            {ob.dossier.map((x) => (
              <div key={x.id} className="rec-docrow">
                <div className="nm">{x.nm}{x.by && <span>validé par {x.by}</span>}</div>
                <span className={`rec-dst ${x.st === 'ok' ? 'ok' : x.st === 'recu' ? 'recu' : 'manq'}`}>
                  {x.st === 'ok' ? 'Validé' : x.st === 'recu' ? 'Reçu — à valider' : 'Manquant'}
                </span>
                {canEdit && x.st === 'manq' && (
                  <button className="rec-ghost sm" disabled={busy} onClick={() => act.onDoc(ob.id, x.id, 'recu')}>Reçu</button>
                )}
                {canEdit && x.st === 'recu' && (
                  <button className="rec-go sm" disabled={busy} onClick={() => act.onDoc(ob.id, x.id, 'ok')}>Valider</button>
                )}
              </div>
            ))}
          </div>
          <div className="r7-sec">
            <h4>📚 Ressources à partager</h4>
            {ob.resources.map((r, i) => (
              <div key={i} className="ob9-res">
                <span className="ic">{r.ic ?? '📄'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="nm">{r.nm}</div>
                  <div className="wh">{r.when}{r.shared && r.sharedBy ? ` · partagé par ${r.sharedBy}` : ''}</div>
                </div>
                {r.shared
                  ? <span className="rec-dst ok">Partagé</span>
                  : canEdit
                    ? <button className="rec-ghost sm" disabled={busy} onClick={() => act.onResource(ob.id, i)}>Partager</button>
                    : <span className="rec-dst recu">Programmé</span>}
              </div>
            ))}
            {!ob.resources.length && <div className="rec-emptybox">Pas de ressources dans le modèle de ce rôle.</div>}
          </div>
          <div className="r7-sec">
            <h4>🤝 Autour de {ob.candidate.firstName}</h4>
            <div className="ob9-team">
              <div className="ob9-tm">
                <span className="q">👤</span>
                <div><b>{ob.manager ?? 'Manager à préciser'}</b><span>Manager N+1</span></div>
              </div>
              {ob.buddy ? (
                <div className="ob9-tm">
                  <span className="q">🤝</span>
                  <div><b>{ob.buddy.name}</b><span>Buddy — parrainage semaine 1</span></div>
                  {canEdit && <button className="rec-ghost sm" disabled={busy} onClick={() => setBuddyPick(true)}>Changer</button>}
                </div>
              ) : (
                <div className="ob9-tm miss">
                  <span className="q">?</span>
                  <div><b>Buddy à désigner</b><span>parrainage semaine 1</span></div>
                  {canEdit && <button className="rec-go sm" disabled={busy} onClick={() => setBuddyPick(true)}>Désigner</button>}
                </div>
              )}
              {buddyPick && (
                <div className="rec-field" style={{ marginTop: 4 }}>
                  <label>Buddy — compte staff</label>
                  <select
                    autoFocus
                    value={ob.buddy?.id ?? ''}
                    onChange={(e) => { act.onBuddy(ob.id, e.target.value || null); setBuddyPick(false); }}
                  >
                    <option value="">— personne —</option>
                    {interviewers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>
          <div className="r7-sec" style={{ '--sc': '#0a9d72' } as React.CSSProperties}>
            <h4>🪪 Module Employés</h4>
            {ob.employee ? (
              <div className="ob9-handoff done">
                <div className="big">✓ Fiche <b>{ob.employee.code}</b> créée{ob.handedAt ? ` le ${new Date(ob.handedAt).toLocaleDateString('fr-FR')}` : ''}</div>
                <p>Salaire de l’offre repris, période d’essai initialisée — les points de contrôle se suivent dans l’onglet « Période d’essai » et la décision finale met à jour le module Contrats. Rattachement organigramme à faire depuis la fiche.</p>
              </div>
            ) : (
              <div className="ob9-handoff">
                <p>
                  Crée la fiche employé chez {ent?.name ?? ob.entityId} : salaire de l’offre acceptée,
                  date d’entrée au {recFmtDate(ob.start)}, période d’essai initialisée (tracker Contrats),
                  {ob.contrat.startsWith('CDD') ? ' terme CDD daté,' : ''} historique salarial ouvert.
                  Les autres candidatures encore ouvertes sur ce poste sont clôturées « Meilleur candidat retenu », avec une réponse négative préparée en brouillon pour chacune.
                </p>
                {manq > 0 && <div className="rec-refusnote" style={{ margin: '8px 0' }}>🗂 {manq} pièce{manq > 1 ? 's' : ''} du dossier encore manquante{manq > 1 ? 's' : ''} — le handoff reste possible.</div>}
                {canEmp
                  ? <button className="rec-go" disabled={busy} onClick={() => act.onHandoff(ob.id)}>Créer la fiche employé →</button>
                  : <span className="rec-hint">Réservé à l’owner (« Vérification & offre » + « Fiches employés »).</span>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── vue Onboarding (cohorte + portal détail) ────────────────
type Filter = 'all' | 'pre' | 'post';

export function RecOnboardingView({ obs, dark, canEdit, canEmp, busy, interviewers, act, focusId }: {
  obs: RecOnboardingDTO[]; dark: boolean; canEdit: boolean; canEmp: boolean; busy: boolean;
  interviewers: RecInterviewerDTO[]; act: ObActions; focusId?: string | null;
}) {
  const [f, setF] = useState<Filter>('all');
  const [sel, setSel] = useState<string | null>(focusId ?? null);
  // v2 — onglet du détail : onboarding (RH) ou période d'essai.
  const [tab, setTab] = useState<'ob' | 'essai'>(focusId ? 'essai' : 'ob');
  const manq = obs.reduce((n, o) => n + obDocsMissing(o), 0);
  const late = obs.reduce((n, o) => n + obLate(o), 0);
  const pre = obs.filter((o) => obDays(o) < 0);
  const nextStart = pre.length ? Math.min(...pre.map((o) => -obDays(o))) : null;
  const kpis: Array<[string | number, string, string]> = [
    [obs.length, 'Recrues en onboarding', 'var(--shf)'],
    [pre.length, 'En pré-boarding', 'var(--info)'],
    [manq, 'Pièces de dossier à compléter', manq ? 'var(--warn)' : 'var(--ink4)'],
    [late, 'Tâches en retard', late ? 'var(--warn)' : 'var(--ink4)'],
    [nextStart != null ? `J−${nextStart}` : '—', 'Prochain démarrage', 'var(--green)'],
  ];
  const FIL: Array<[Filter, string]> = [['all', 'Toutes'], ['pre', 'Avant J1'], ['post', 'En poste']];
  const match = (o: RecOnboardingDTO, k: Filter) => k === 'all' || (k === 'pre' ? obDays(o) < 0 : obDays(o) >= 0);
  const list = obs.filter((o) => match(o, f));
  const selOb = sel ? obs.find((o) => o.id === sel) ?? null : null;
  const selPh = selOb ? REC_OB_PHASES[obPhase(selOb)] : null;
  return (
    <div>
      <div className="d11-kpis ob-kpis">
        {kpis.map(([v, l, c], i) => (
          <div key={i} className="d11-kpi" style={{ '--kc': c, cursor: 'default' } as React.CSSProperties}>
            <span className="tx"><b>{v}</b><span className="l">{l}</span></span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 14px', flexWrap: 'wrap' }}>
        <div className="rec-seg">
          {FIL.map(([k, l]) => {
            const n = obs.filter((o) => match(o, k)).length;
            return (
              <button key={k} className={f === k ? 'on' : ''} onClick={() => setF(k)}>
                {l}{k !== 'all' && n > 0 && <span className="bdg">{n}</span>}
              </button>
            );
          })}
        </div>
        <span className="rec-hint" style={{ marginLeft: 'auto' }}>
          Chaque recrue suit le plan du modèle de son rôle — dossier, ressources, 1:1 et points d’essai jusqu’à la décision finale écrite
        </span>
      </div>
      <div className="ob-grid">
        {list.map((ob, i) => <ObCard key={ob.id} ob={ob} i={i} onOpen={() => setSel(ob.id)} />)}
        {!list.length && (
          <div className="rec-emptybox big" style={{ gridColumn: '1/-1' }}>
            🧳 Personne dans ce filtre — l’onboarding se crée à l’acceptation d’une offre.
          </div>
        )}
      </div>
      {selOb && selPh && createPortal(
        <div className={`recapp rec-portal${dark ? ' theme-dark' : ''}`}>
          <div className="rec-ov" onClick={() => setSel(null)}></div>
          <div className="r3-panel r8-full" style={{ '--sc': selPh.c } as React.CSSProperties}>
            <div className="r7-head">
              <ObAv ob={selOb} big />
              <div style={{ minWidth: 0 }}>
                <div className="r7-title">Onboarding — {selOb.candidate.firstName} {selOb.candidate.lastName}</div>
                <div className="r7-sub">{selOb.roleLabel} · {entOf(selOb.entityId)?.name}{selOb.manager ? ` · manager ${selOb.manager}` : ''}</div>
              </div>
              <span className="r6-st" style={{ '--stc': selPh.c, background: selPh.c } as React.CSSProperties}>{selPh.ic} {selPh.lbl}</span>
              <span className="ob9-hcount">{obJ(obDays(selOb))}</span>
              <div style={{ marginLeft: 'auto' }}>
                <button className="rec-x" onClick={() => setSel(null)}>✕</button>
              </div>
            </div>
            <div className="r7-body">
              <div className="rec-seg" style={{ marginBottom: 16 }}>
                <button className={tab === 'ob' ? 'on' : ''} onClick={() => setTab('ob')}>Onboarding</button>
                <button className={tab === 'essai' ? 'on' : ''} onClick={() => setTab('essai')}>Période d’essai</button>
              </div>
              {tab === 'ob'
                ? <ObDetail ob={selOb} canEdit={canEdit} canEmp={canEmp} busy={busy} interviewers={interviewers} act={act} />
                : <ProbationSection ob={selOb} canRh={canEdit} />}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
